/**
 * vqeCopy — the single canonical, user-facing copy table for the Visual Query
 * Editor. Every string a user can read (disabled-control tooltips, empty-state
 * bodies, confirm dialogs, toasts, add/remove button labels) lives here so the
 * product voice stays consistent and project codenames / roadmap labels never
 * leak into the UI.
 *
 * Shape is i18n-ready: a nested `as const` object literal keyed by domain
 * (`disabled`, `empty`, `confirm`, `toast`, `actions`). Strings that need
 * runtime values are exposed as pure functions.
 *
 * Render modules import this via the feature barrel
 * (`import { vqeCopy } from '../..'`). NONE of the leaf strings may contain a
 * project codename — see `vqeCopy.test.ts`, which enforces that mechanically.
 */
import type { StatementKind } from '../../../sql-grammar/model';
import type { DmlWhereScope } from '../render/common/dmlWhereScope';

/**
 * User-facing label for each parsed statement kind. Surfaced in the statement
 * picker menu so users see "SELECT" / "INSERT" etc. rather than the internal
 * kebab-case kind ids. Anything unmapped falls back to a neutral "Statement".
 */
const STATEMENT_KIND_LABELS: Partial<Record<StatementKind, string>> = {
  select: 'SELECT',
  insert: 'INSERT',
  update: 'UPDATE',
  delete: 'DELETE',
  merge: 'MERGE',
  'ddl-create': 'CREATE',
  'ddl-alter': 'ALTER',
  'ddl-drop': 'DROP',
  'ddl-truncate': 'TRUNCATE',
  'ddl-rename': 'RENAME',
  grant: 'GRANT',
  revoke: 'REVOKE',
  'transaction-control': 'Transaction',
  'session-setting': 'Session',
  explain: 'EXPLAIN',
  call: 'CALL',
  'procedural-body': 'Procedure',
  pragma: 'PRAGMA',
  'batch-separator': 'Batch',
  'client-directive': 'Directive',
  unknown: 'Statement',
};

type DmlVerb = 'UPDATE' | 'DELETE';

/** "changes every row in orders" / "… that the join matches" / "deletes rows in t up to its LIMIT". */
function dmlReach(verb: DmlVerb, target: string, scope: DmlWhereScope): string {
  const does = verb === 'DELETE' ? 'deletes' : 'changes';
  if (scope === 'limited') return `${does} rows in ${target} up to its LIMIT`;
  return `${does} every row in ${target}${scope === 'join-matched' ? ' that the join matches' : ''}`;
}

export const vqeCopy = {
  /**
   * Tooltip copy for controls that are visible but disabled. Single-line.
   * Where it fits, follows the template
   *   "<what the control does> · <why disabled> · <how to enable>".
   */
  disabled: {
    noConnection:
      'Pick from this list · no database is connected · connect to a database to populate suggestions',
    noSnapshot:
      'Refresh suggestions · no schema snapshot is loaded yet · connect or refresh the schema cache',
    /**
     * Kind-aware one-line message shown on the DML parse-error escape panel
     * (DmlParseErrorPanel) when an INSERT/UPDATE/DELETE/MERGE can't be parsed
     * into the structured editor. Pairs with the "Edit as SQL" escape button.
     */
    dmlParseError: (kind: 'insert' | 'update' | 'delete' | 'merge'): string => {
      const labels: Record<'insert' | 'update' | 'delete' | 'merge', string> = {
        insert: 'INSERT',
        update: 'UPDATE',
        delete: 'DELETE',
        merge: 'MERGE',
      };
      return `This ${labels[kind]} statement couldn’t be parsed into the visual editor — open “Edit as SQL” to keep working on it.`;
    },
    /**
     * Honest read-only notice shown below the column map when an INSERT's
     * body keyword isn't one VQE recognizes (e.g. MSSQL's `OUTPUT` clause —
     * decision 2, §2.4 S6). The column map still renders normally; only the
     * VALUES/SELECT body is unrenderable here.
     */
    insertBodyUnknown:
      'This INSERT’s value list couldn’t be recognized here (for example, an OUTPUT clause) — open “Edit as SQL” to see the full statement.',
    dragSchemaTreeUnavailable:
      'Drop a schema-tree item to add it · drag-and-drop isn’t available here · use the “Add” buttons instead',
    /**
     * Generic fallback used wherever a control is scaffolded but not yet wired.
     * Replaces the previous "Editing ships in <codename>" strings.
     */
    editingUnavailable:
      'Edit visually · this control isn’t editable here yet · open “Edit as SQL” to change it directly',
    /** Filter-specific variant of {@link disabled.editingUnavailable}. */
    filterEditingUnavailable:
      'Edit this filter · filter editing isn’t available here yet · open “Edit as SQL” to change it directly',
    /** Column-specific variant of {@link disabled.editingUnavailable}. */
    columnEditingUnavailable:
      'Edit this column · column editing isn’t available here yet · open “Edit as SQL” to change it directly',
    /**
     * CTE-chip variant of {@link disabled.editingUnavailable}: only a
     * SELECT-shaped CTE body opens in the structured editor — `CteCard`'s
     * `canDrillBody` gates the full editor on the same `bodyKind === 'select'`
     * condition, so a data-modifying CTE body (PG `WITH d AS (DELETE …)`) is
     * not drillable on either surface.
     */
    cteBodyEditingUnavailable:
      'Edit this CTE · only SELECT bodies can be edited here · open “Edit as SQL” to change it directly',
    /** Remove-button variant shown on a SELECT column chip when editing isn't ready (no editContext / no schema snapshot yet). */
    removeColumnNotReady:
      'Remove this column · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”',
    /** Remove-button variant shown on an ORDER BY entry when editing isn't ready (no editContext / no schema snapshot yet). */
    removeSortNotReady:
      'Remove this sort · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”',
    /** Reference scan could not run; removal hard-disabled to avoid corruption. */
    removeUnverified:
      'Can’t verify whether this CTE is referenced — removal disabled to avoid breaking the query',
    /** OVER structured field editing is ready once connected + schema-loaded;
     *  this is the not-ready gate shown on a disconnected tab (WindowSpec). */
    windowStructuredUnavailable:
      'Edit OVER fields · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”',
    /**
     * [+ add join] lists FK-linked tables (auto ON) plus every other reachable
     * table (pick the ON columns). It is only disabled when there is no other
     * table in scope to join at all.
     */
    addJoinNoTables:
      'Add a JOIN · no other table is in scope to join · refresh the schema or pick a database with tables',
    /** [+ add cross-join] disabled because no schema snapshot is loaded. */
    addCrossJoinNoSnapshot:
      'Add a cross-join · no schema snapshot loaded yet · connect to a database to populate suggestions',
    /** [+ add cross-join] disabled because no other tables are in scope. */
    addCrossJoinNoTables:
      'Add a cross-join · no other tables in scope · refresh the schema or pick a database with tables',
    /**
     * INSERT column-list ops (add/remove-insert-column) require an explicit
     * column list AND a VALUES body (decision 4, §2.4 S6). Shown on the
     * [+ add column] footer when the INSERT omits its column list — an
     * implicit list (`INSERT INTO t VALUES …`) or an explicit-but-empty one.
     */
    insertColumnsNoExplicitList:
      'Add a column · this INSERT has no explicit column list · open “Edit as SQL” to add one',
    /**
     * INSERT column-list ops disabled because the body is an INSERT…SELECT —
     * the listed columns are populated from the source query, not literal
     * VALUES rows (decision 4).
     */
    insertColumnsSourceQuery:
      'Edit these columns · this INSERT populates them from a SELECT query · edit the source query instead',
    /** [remove column] disabled when only one column remains (decision 5). */
    insertColumnsLastOne:
      'Remove this column · at least one column must remain · edit the column list via “Edit as SQL” instead',
    /**
     * [+ add column] disabled because every target-table column is already in
     * the INSERT column list (§2.4 S6b review follow-up). Editing itself IS
     * available — the generic editingUnavailable fallback would state the
     * wrong reason. Sibling of {@link disabled.returningAllColumnsListed},
     * whose "add an expression" remedy doesn't fit a column list.
     */
    insertColumnsAllListed:
      'Add a column · every column on this table is already listed · remove one first if you need to re-add it',
    /**
     * Conflict-tail assignment remove (ON CONFLICT DO UPDATE / ON DUPLICATE
     * KEY UPDATE) disabled at one remaining — an empty SET/UPDATE list is
     * ill-formed SQL (§2.4 S6b, decision-5 style).
     */
    conflictAssignmentsLastOne:
      'Remove this assignment · at least one assignment must remain · open “Edit as SQL” to remove the whole clause',
    /**
     * Hint shown while the DO NOTHING → DO UPDATE action switch waits for its
     * seed assignment — the switch only commits once the form is submitted
     * (§2.4 S6b, decision 9).
     */
    conflictDoUpdateNeedsAssignment:
      'Switch to DO UPDATE · the update needs a first assignment · add one to complete the switch',
    /** Conflict-target picker Apply disabled with an empty selection (§2.4 S6b). */
    conflictTargetNeedsColumn:
      'Apply the conflict target · at least one column is required · pick one or more columns',
    /**
     * RETURNING add-picker disabled because every target-table column is
     * already in the RETURNING list (§2.4 S6b). Editing itself IS available —
     * the generic editingUnavailable fallback would state the wrong reason.
     */
    returningAllColumnsListed:
      'Add a RETURNING column · every column on this table is already returned · open “Edit as SQL” to add an expression instead',
    /**
     * Set-operator separator dropdown (§2.3 S7) disabled variant shown when an
     * editContext exists but the schema snapshot isn't loaded yet — distinct
     * from {@link disabled.editingUnavailable}, which stays the copy when no
     * editContext is wired at all (mirrors the `removeColumnNotReady` family).
     */
    setOperatorNotReady:
      'Change the operator · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”',
    /**
     * MERGE WHEN-branch action-kind switch (§2.5 S7) — three structural
     * disabled reasons plus the standard not-ready variant. Structural
     * reasons (a NOT MATCHED branch's INSERT-only shape, Oracle's blanket
     * veto) take priority over readiness — they stay true even once ready.
     */
    mergeActionNotReady:
      'Switch the action · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”',
    mergeActionInsertOnly:
      'Switch the action · this branch only supports INSERT · open “Edit as SQL” to change it directly',
    mergeActionOracle:
      'Switch the action · Oracle MERGE doesn’t support switching a branch’s action here · open “Edit as SQL” to change it directly',
    /** MERGE `[+ Add WHEN]` menu (§2.5 S7) disabled variants. */
    mergeAddBranchNotReady:
      'Add a WHEN branch · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”',
    mergeAddBranchOracleFull:
      'Add a WHEN branch · this MERGE already has one MATCHED and one NOT MATCHED branch · Oracle allows at most one of each',
    /** MERGE per-branch remove button (§2.5 S7) disabled variants. */
    mergeRemoveLastBranch:
      'Remove this WHEN branch · at least one branch must remain · edit the statement via “Edit as SQL” instead',
    mergeRemoveNotReady:
      'Remove this WHEN branch · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”',
  },

  /**
   * Readiness banner copy. A single banner above the clause cards (and a 1-line
   * "pip" variant in the compact popover) explains, in one place, why editing is
   * disabled and how to enable it — replacing N identical per-control tooltips.
   * Each non-ready state carries a `body` (full variant) + a short `pip` line +
   * an optional CTA `label`.
   */
  banner: {
    noConnection: {
      body: 'Editing is disabled — this tab isn’t connected to a database.',
      pip: 'Not connected — editing disabled.',
      cta: 'Connect…',
    },
    snapshotLoading: {
      body: 'Loading schema — editing enables automatically once it lands.',
      pip: 'Loading schema…',
    },
    noDatabase: {
      body: 'No database is selected for this tab, so there is no schema to edit with.',
      pip: 'No database selected — choose one in the status bar.',
      cta: 'Choose database',
    },
    snapshotFailed: {
      body: 'The table list for this database couldn’t be loaded, so editing is off.',
      pip: 'Schema didn’t load — editing off.',
      cta: 'Reload schema',
    },
    columnsFailed: {
      body: 'Tables loaded, but their columns couldn’t be read, so column lists may be missing or incomplete.',
      pip: 'Columns didn’t load.',
      cta: 'Reload schema',
    },
    readOnly: {
      body: 'This statement renders read-only — edit it as SQL to make changes.',
      pip: 'Read-only — edit as SQL.',
      cta: 'Edit as SQL',
    },
    /** aria-label for the banner's dismiss (×) control. */
    dismissLabel: 'Dismiss',
  },

  /**
   * Starter-canvas copy (Task 4, §3.2 S9) — shown when a `visual-query` tab
   * mounts with no rendered statement (blank/whitespace-only buffer). Replaces
   * the old dev-stub `workspace.stubTitle`/`stubBody` pair with a real
   * "pick a table" affordance.
   */
  canvas: {
    title: 'Build a query',
    body: 'Pick a table to start from, or open the SQL editor — everything here stays plain SQL.',
    startFromTable: 'Start from a table',
    startFromSql: 'Start from SQL',
    searchPlaceholder: 'Search tables…',
    /** Honest empty-schema message — shown once the schema snapshot has
     *  loaded but the database has zero tables. Doubles as the table
     *  picker's `emptyMessage` (§3.2 review follow-up: a connected-but-empty
     *  database must never look like an eternal "still loading" spinner). */
    noTables: 'No tables in this database yet — reload the schema or pick another database.',
    /** The canvas's own empty-database line — its two ways out are buttons right under it. */
    emptyDatabase: 'No tables in this database yet.',
    /** Same verb as the readiness banner's CTA (`banner.*.cta`) — one name for one action. */
    reloadSchema: 'Reload schema',
    chooseDatabase: 'Choose another database',
    /** Replaces `body` when the table list failed to load: the table picker is hidden and the
     *  banner above carries Reload schema, so the canvas points at the two ways on. */
    loadFailed: 'Tables could not be loaded — reload the schema or start from SQL.',
  },

  /**
   * Statement-capability copy (EDG-01/02/03/04/29 — MERGE support varies by
   * engine). Shown INSIDE the statement canvas (`MergeRenderer`), not only in
   * a hand-off dialog, so the dialect truth is visible wherever the statement
   * is edited. `merge.unsupportedBanner` names the engine and the consequence
   * ("will fail"); `merge.versionHint` covers PostgreSQL, whose MERGE support
   * is version-gated (15+) rather than absent — VQE has no reliably-detected
   * connection version to gate on yet, so this stays an honest hint rather
   * than a false "unsupported" claim.
   */
  capability: {
    merge: {
      /** Loud in-canvas banner for engines that have no MERGE statement at all (mysql, sqlite). */
      unsupportedBanner: (engineName: string): string =>
        `${engineName} doesn’t support MERGE — this statement will fail on this connection.`,
      /** Quiet in-canvas note for engines whose MERGE support is version-gated (postgres 15+). */
      versionHint: (engineName: string, minVersion: string): string =>
        `${engineName} added MERGE in version ${minVersion} — this connection’s version isn’t checked automatically, so confirm your server meets it before running.`,
      /**
       * Primary in-canvas CTA (EDG-01/02/03/04) — offered when this MERGE fits
       * the convertible subset. Rewrites it in place as the engine's native
       * upsert (`INSERT … ON DUPLICATE KEY UPDATE` / `INSERT … ON CONFLICT DO
       * UPDATE`) so it lands in the real upsert editor instead of a doomed
       * MERGE canvas.
       */
      convertCta: 'Convert to native upsert',
      /**
       * MySQL-only caveat shown beneath the convert CTA (I6). MySQL's
       * `ON DUPLICATE KEY UPDATE` only *updates* a matched row when a UNIQUE
       * index / PRIMARY KEY covers the conflict key columns; without one it
       * silently inserts a duplicate — unlike PostgreSQL / SQLite `ON CONFLICT`,
       * which errors loudly. The schema snapshot carries no unique-index info,
       * so this is a truthful caveat, not a static gate.
       */
      convertMysqlKeyCaveat:
        'On MySQL this becomes ON DUPLICATE KEY UPDATE — it only updates a matched row when a UNIQUE index or PRIMARY KEY covers the key column(s). Without one, MySQL silently inserts a duplicate instead of updating.',
      /**
       * Honest, per-reason line shown when the MERGE can't be auto-converted.
       * Each names the exact blocking construct + the real next step (Edit as
       * SQL) — no dialect lie, no version claim. Keyed on the pure transform's
       * `MergeConvertReason`.
       */
      notConvertible: (reason: string): string => {
        const messages: Record<string, string> = {
          'delete-branch':
            'Can’t auto-convert: a WHEN MATCHED → DELETE arm has no ON DUPLICATE KEY / ON CONFLICT equivalent — open “Edit as SQL” to adapt it by hand.',
          'not-matched-by-source':
            'Can’t auto-convert: a WHEN NOT MATCHED BY SOURCE arm changes rows with no source match, which an upsert can’t express — open “Edit as SQL” to adapt it.',
          'multiple-matched-branches':
            'Can’t auto-convert: this MERGE has more than one WHEN MATCHED arm, but an upsert has a single update path — open “Edit as SQL” to combine them.',
          'multiple-not-matched-branches':
            'Can’t auto-convert: this MERGE has more than one WHEN NOT MATCHED arm, but an upsert inserts one row shape — open “Edit as SQL” to adapt it.',
          'no-update-branch':
            'Can’t auto-convert: this MERGE has no WHEN MATCHED → UPDATE arm to become the upsert’s update — open “Edit as SQL” to adapt it.',
          'no-insert-branch':
            'Can’t auto-convert: this MERGE has no WHEN NOT MATCHED → INSERT arm to become the upsert’s insert — open “Edit as SQL” to adapt it.',
          'subquery-source':
            'Can’t auto-convert: the USING source is a subquery or derived table — turn it into a plain table first, or open “Edit as SQL”.',
          'non-equality-on':
            'Can’t auto-convert: the ON predicate isn’t a plain column equality, so there’s no conflict key to build the upsert from — open “Edit as SQL” to adapt it.',
          'insert-missing-columns':
            'Can’t auto-convert: the WHEN NOT MATCHED → INSERT needs an explicit column list and VALUES — open “Edit as SQL” to add them.',
          'matched-set-unsupported':
            'Can’t auto-convert: the WHEN MATCHED SET uses a value an upsert can’t copy as-is (a computed expression, or a column that isn’t inserted) — open “Edit as SQL” to adapt it.',
          'matched-set-diverges-from-insert':
            'Can’t auto-convert: WHEN MATCHED sets a column to a different value than the INSERT provides, which ON CONFLICT / ON DUPLICATE KEY can’t express — open “Edit as SQL” to adapt it by hand.',
          'conditional-branch':
            'Can’t auto-convert: a WHEN … AND <condition> arm applies only to rows matching that condition, but a plain upsert has no per-row branch condition — open “Edit as SQL” to adapt it.',
          'quoted-identifier':
            'Can’t auto-convert: this MERGE uses quoted column identifiers, which the upsert rewrite can’t preserve safely across engines — open “Edit as SQL” to convert it by hand.',
        };
        return (
          messages[reason] ??
          'Can’t auto-convert this MERGE to a native upsert — open “Edit as SQL” to adapt it by hand.'
        );
      },
      /**
       * Execute-gate warning reason (EDG-02/04 Execute halves) — routed through
       * the shared destructive-confirm dialog so an unsupported-engine MERGE
       * warns before it runs (never a hard block: the user may be drafting it
       * for another engine). Truthful: names the engine + the real outcome.
       */
      executeWarning: (engineName: string): string =>
        `${engineName} doesn’t support MERGE — running this on this connection will fail. Run it anyway?`,
    },
    /**
     * Named `WINDOW w AS (…)` clause capability copy (ANA-01/02). All five
     * engines support the clause, but SQL Server (2022) and Oracle (21c) added
     * it later than PostgreSQL / MySQL / SQLite. VQE has no reliably-detected
     * connection version, so — mirroring `capability.merge.versionHint` — this
     * is a quiet, truthful hint rather than a false "unsupported" claim.
     */
    window: {
      versionHint: (engineName: string, minVersion: string): string =>
        `${engineName} added named WINDOW clauses in version ${minVersion} — this connection’s version isn’t checked automatically, so confirm your server meets it before running.`,
      /**
       * ANA-06 — the truthful "why is Open full editor offered here" hint on the
       * compact popover's standing CTA when the statement has an INLINE window
       * whose column is locked read-only (per-column degrade). It names the
       * construct (window column), the reason (read-only in the quick editor),
       * the next action (edit its OVER clause in the full editor), AND the fact
       * that every other clause stays editable right here — so the CTA no longer
       * silently appears for a window statement with no explanation. (A NAMED
       * window instead full-degrades and the degrade card owns that "why", so
       * this line is only shown for the per-column-locked inline case.)
       */
      compactLockedHint:
        'This window column is read-only in the quick editor — open the full editor to change its OVER clause. Every other clause here stays editable.',
    },
  },

  /**
   * DML blast-radius safety copy (Advisor 2 §A2 + §A4).
   * `dmlScope` — an UPDATE / DELETE with no WHERE: the red badge on its
   * target row (and in the compact header), whose tooltip + accessible name
   * spell out the reach, and the empty WHERE row's own sentence — so the
   * warning is never a bare badge. `scope` (`dmlWhereScope`): a JOIN / USING /
   * FROM list or a MySQL LIMIT bounds the rows, where a plain "every row in
   * <target>" would overclaim.
   * `dmlEstimate` — the click-to-arm row-count chip's teaching tooltips + labels.
   */
  dmlScope: {
    /** Red badge text. */
    badge: (scope: DmlWhereScope): string => (scope === 'all-rows' ? 'No WHERE · all rows' : 'No WHERE'),
    /** The badge's tooltip + accessible name. */
    hint: (verb: DmlVerb, target: string, scope: DmlWhereScope): string =>
      `This ${verb} has no WHERE clause, so it ${dmlReach(verb, target, scope)}.`,
    /** Body of the empty WHERE row, after its WHERE label ("WHERE — none, so this UPDATE …"). */
    emptyWhere: (verb: DmlVerb, target: string, scope: DmlWhereScope): string =>
      `— none, so this ${verb} ${dmlReach(verb, target, scope)}`,
  },

  dmlEstimate: {
    /** Unarmed chip label — click to arm (no auto-count). */
    armLabel: 'Estimate rows',
    /** Tooltip on the unarmed chip explaining what arming does. */
    armHint:
      'Estimate how many rows this statement affects — runs a COUNT with the same filter.',
    /** Suffix for an exact single-table estimate, e.g. "≈ 1,204 rows". */
    rows: 'rows',
    /** Tooltip on a finished exact estimate (the same explanation as the arm hint, past tense). */
    readyHint:
      'How many rows this statement affects — counted with a COUNT that uses the same filter.',
    /** Label body for a join-form estimate, e.g. "~1,204 matching rows". */
    matchingRows: 'matching rows',
    /** Prefix for the empty-WHERE fusion, e.g. "all rows (≈ 58,900)". */
    allRows: 'all rows',
    /**
     * Tooltip for an approximate (upper-bound) estimate. Covers both the
     * join-form over-count and a trailing MySQL `LIMIT n` that caps how many
     * rows are actually touched (I7) — either way the COUNT can exceed the
     * number of rows changed.
     */
    approximate:
      'This count is an upper bound — the statement may change fewer rows. A multi-table join counts matching row combinations, and a trailing LIMIT caps how many rows are actually touched.',
    /** Chip label for a shape that can't be probed safely. */
    notApplicableLabel: 'n/a for this shape',
    /** Tooltip for the n/a state — teaches why, and that Execute is unaffected. */
    notApplicable:
      'No safe row estimate for this statement shape (for example, a correlated subquery) — Execute still works normally.',
    /** Tooltip shown when the probe fails or times out. */
    timeout: 'Estimate timed out after 3 s — the table may be large. Execute still works normally.',
  },

  /**
   * Correlation-pill tooltip copy (DML-20 + sqlite twin). Shown on a WHERE
   * pill that connects the DML target to a FROM/USING relation — an
   * `UPDATE … FROM` / `DELETE … USING` join predicate. Removing it doesn't
   * just drop a filter; it can turn a targeted statement into a
   * near-cartesian one, so the pill earns its own always-visible warning
   * (independent of the guarded-remove dialog, which fires on click).
   */
  dmlCorrelation: {
    tooltip: (other: string, target: string, verb: 'update' | 'delete'): string =>
      `This condition connects ${other} to ${target} — removing it can make the ${verb} touch far more rows.`,
  },

  /** Inline hint lines shown beneath read-only / opaque content. */
  hint: {
    /** Shown under a window function's OVER-clause body in the READ-ONLY state
     *  (no connection / no schema yet). Teaches that the PARTITION BY / ORDER BY
     *  / frame become editable once ready — it is NOT shown above the structured
     *  editor, so it must not claim "edited as text" (the ANA-14 contradiction). */
    windowCustomFrame: 'Window frame — connect and load the schema to edit its PARTITION BY / ORDER BY / frame here.',
    /**
     * Section-level prompt shown in the SELECT card header when the query mixes
     * aggregate and non-aggregate columns and `count` non-aggregate columns are
     * not yet in GROUP BY. Pairs with the "Fix all" button.
     */
    aggregateNotInGroupBy: (count: number): string =>
      `${count} ${count === 1 ? 'column isn’t' : 'columns aren’t'} in GROUP BY`,
    /** Tooltips (and accessible names) of the "+ or" / "+ and" condition chips. */
    groupWithOr: 'Add an alternative to this condition (OR)',
    groupWithAnd: 'Add a condition that must also match (AND)',
    addToOrGroup: 'Add another alternative to this group (OR)',
    addToAndGroup: 'Add another condition to this group (AND)',
    /** Tooltip on the LIMIT/OFFSET/TOP/FETCH panel's inline-editable value button. */
    limitInlineEdit: 'Click to edit — Enter commits, Esc cancels',
    /** Tooltip on the SELECT card's DISTINCT badge when it becomes a toggle button. */
    distinctToggle: (on: boolean): string =>
      on ? 'DISTINCT is on — click to return all rows' : 'DISTINCT is off — click to return only unique rows',
    /**
     * INSERT VALUES-grid honesty warning (DML-36, Advisor 2 §A7 third
     * bullet). Shown above the grid ONLY when the connection is Oracle AND
     * the statement has more than one VALUES row. The VQE has no INSERT-ALL
     * emitter — `addInsertValuesRow` just appends a bare `(…)` tuple — so a
     * 2nd+ row produces `VALUES (…),(…)`, which fails on Oracle before 23ai
     * (Oracle 23c/23ai added the table value constructor, so multi-row
     * VALUES succeeds there — this project's own test Oracle is 23ai/26ai).
     * VQE has no reliably-detected connection version to gate on, so the
     * copy stays version-conditional rather than a flat "fails" claim, per
     * the same pattern as `capability.merge.versionHint` above. This is a
     * warning, never a claim that INSERT ALL / UNION ALL emulation actually
     * happens.
     */
    oracleMultiRowValues:
      'Before Oracle 23ai, Oracle has no multi-row VALUES — on older servers this INSERT fails as written. Use INSERT ALL, UNION ALL, or one INSERT per row. This connection’s version isn’t checked automatically.',
    /**
     * Column-pill remove `×` consequence tooltip (DML-37) shown only in the
     * ENABLED case — removing a column shifts every VALUES row's positional
     * mapping. `columnRemoveConsequence` is the concrete count-bearing
     * variant used when the VALUES row count is cheaply threaded in;
     * `columnRemoveConsequenceGeneric` is the count-free fallback for mounts
     * that don't thread it.
     */
    columnRemoveConsequence: (rowCount: number): string =>
      `Removes this column from all ${rowCount} row${rowCount === 1 ? '' : 's'} — values map by position, so the rest shift left.`,
    columnRemoveConsequenceGeneric:
      'Removes this column from every VALUES row — values map by position, so the rest shift left.',
    /**
     * INSERT…SELECT column-pairing card copy (DML-10 / DML-11). The pairing
     * card renders the target column list against the source SELECT's items
     * positionally so a misalignment (or a missing/extra expression) is
     * visible instead of silent until execution.
     */
    insertSelect: {
      /**
       * Amber mismatch note shown when the explicit target column count and
       * the source SELECT's item count differ. This is a hard error on ALL
       * 5 engines (MSSQL, PostgreSQL, MySQL, Oracle, SQLite each reject an
       * INSERT…SELECT whose column counts don't match), so the wording holds
       * without any engine-specific hedge.
       */
      countMismatch: (targetCount: number, sourceCount: number): string =>
        `The source query returns ${sourceCount} column(s) but ${targetCount} target column(s) are listed — every engine rejects an INSERT…SELECT whose column counts don’t match. Align them so each source expression maps to one target column.`,
      /**
       * Neutral (non-error) note shown when the source SELECT uses `*` — its
       * column count can't be counted statically, so the pairing card can't
       * zip it against the target list. Names the target count when known so
       * there's something concrete to verify against.
       */
      starNote: (targetCount: number | null): string =>
        targetCount === null
          ? 'The source query selects * — its column count is only known once it runs, and this INSERT also has no explicit column list, so check both against the table’s declared column order.'
          : `The source query selects * — its column count is only known once it runs. Confirm it resolves to ${targetCount} column${targetCount === 1 ? '' : 's'} to match the target column${targetCount === 1 ? '' : 's'} listed above.`,
      /**
       * One-line note shown alongside the positional-only pairing list when
       * the INSERT omits its explicit column list — the target names aren't
       * known here, only their declared order on the table.
       */
      implicitListNote: (sourceCount: number): string =>
        `This INSERT has no explicit column list, so target names aren’t shown here — its ${sourceCount} source column${sourceCount === 1 ? '' : 's'} map positionally to the table’s declared column order.`,
    },
  },

  /** Placeholder copy for not-yet-populated option lists / empty bodies. */
  placeholder: {
    /** Empty <option> shown before schema suggestions are available (FilterBuilder). */
    /**
     * Filter chip value input, by the value's type (`VqeFilterChipBuilder`).
     * The Table Explorer keeps its own "Filter…" (PP-11).
     */
    filterValueNumber: 'Number',
    filterValueText: 'Text',
    filterValue: 'Value',
  },

  /** Copy for `CommandPickerPopover` group headings and related picker chrome. */
  picker: {
    /**
     * Heading for the group of aggregate-function templates (`COUNT(*)`,
     * `SUM(column…)`, …) that leads the HAVING filter chip's field step —
     * see `AggregateFieldTemplates.tsx`.
     */
    aggregatesHeading: 'Aggregates',
    /**
     * ON CONFLICT action-switch option labels (§2.4 S6b). Rendered exactly as
     * the SQL keywords they emit — the switch is a syntax control, not prose.
     */
    conflictActionDoNothing: 'DO NOTHING',
    conflictActionDoUpdate: 'DO UPDATE',
    /**
     * Column pickers over sources whose columns are not known — a CTE, a
     * derived table, a table function, or a table missing from the schema
     * cache (`useInScopeColumnPicker`). `sources` reads `r (CTE), t (derived
     * table)`. Shown instead of unrelated tables, whose columns would emit SQL
     * the statement cannot run.
     */
    columnsUnknown: (sources: string): string =>
      `No columns to list. The columns of ${sources} are not known here — edit the SQL to use them.`,
    /** Footer note when some sources are listed and others are not. */
    columnsNotListed: (sources: string): string => `Not listed: the columns of ${sources}.`,
    sourceKindCte: 'CTE',
    sourceKindDerived: 'derived table',
    sourceKindFunction: 'table function',
    sourceKindUncached: 'not in the schema cache',
    /** "Choose columns" hover text on Oracle when a USING / NATURAL column's name is ambiguous in `*` (`SelectStarExpand`). */
    starMergedJoin: "Not available: a shared column is ambiguous and Oracle can't qualify it.",
    /** "Choose columns" hover text on MySQL over a FULL JOIN (MySQL reads FULL as the left table's alias). */
    starNoFullJoin: 'Not available: MySQL has no FULL JOIN.',
    /** Hover text of the "Loose FK" badge on an [+ add join] candidate (`JoinCandidateGroups`). */
    looseJoinHint: 'Linked by a loose relationship declared in MetaInfo — ON is auto-derived',
    looseJoinFilteredHint:
      "Linked by a loose relationship declared in MetaInfo — ON is auto-derived and includes the relationship's filters",
  },

  /** Empty-state body copy for sections with no parsed items. */
  empty: {
    noFilters: 'No filters yet — add one to narrow the rows.',
    /** Empty-state body for the Limit card when no TOP/LIMIT/FETCH exists. */
    noLimit: 'No row limit — every matching row is returned.',
  },

  /** Confirm-dialog copy for destructive edits. */
  confirm: {
    removeOnlyFromSource: {
      title: 'Remove from this statement?',
      body:
        'This removes the item from the statement only — it does not touch any data in your database.',
      apply: 'Remove',
      cancel: 'Cancel',
    },
    removeReferencedCte: {
      title: (name: string, refs: number): string =>
        `Remove “${name}”? It is referenced ${refs} ${refs === 1 ? 'time' : 'times'}.`,
      body:
        'Removing this CTE will leave those references dangling and break the query until you fix them.',
      apply: 'Remove anyway',
      cancel: 'Cancel',
    },
    /**
     * DML guarded-filter-removal confirm (Advisor 2 §A3, DML-20 + the
     * guard-half of DML-01/05/06/08/24/33). Two sibling bodies teach the
     * ACCURATE "why" per trigger, because "no WHERE ⇒ every row" is only true
     * when nothing else remains: this one fires when the WHERE `×` would
     * remove the LAST remaining filter on an UPDATE/DELETE (⇒ a genuinely
     * whole-table statement). When OTHER filters still remain and the
     * removed pill is instead the "correlation" predicate (connects the
     * target to a FROM/USING relation ⇒ near-cartesian), see the sibling
     * `removeCorrelationFilter` below — its premise is the join hazard, not
     * "no WHERE".
     */
    removeLastDmlFilter: {
      title: 'Remove the last filter?',
      body: (verb: 'UPDATE' | 'DELETE', target: string): string =>
        `Without a WHERE filter this ${verb} affects every row in ${target}. Nothing runs until you press Execute, but Execute is one click away.`,
      apply: 'Remove filter',
      cancel: 'Cancel',
    },
    /**
     * DML guarded correlation-removal confirm (Advisor 2 §A3, DML-20) — the
     * sibling of `removeLastDmlFilter` above for the case where OTHER real
     * filters still remain (so the "no WHERE" premise would be FALSE) but the
     * pill being removed IS the correlation predicate (references BOTH the
     * DML target and a FROM/USING relation). Teaches the actual hazard —
     * losing the join condition lets the statement match far more rows than
     * intended — and interpolates the SAME target + related-relation names
     * the pill's own always-visible tooltip already surfaces
     * (`dmlCorrelation.tooltip`).
     */
    removeCorrelationFilter: {
      title: 'Remove this join condition?',
      body: (target: string, relation: string, verb: 'UPDATE' | 'DELETE'): string =>
        `This condition connects ${target} to ${relation} — removing it lets the ${verb} match far more rows than intended. Nothing runs until you press Execute.`,
      apply: 'Remove filter',
      cancel: 'Cancel',
    },
  },

  /**
   * CTE-card copy — enabled-control tooltips + the anchor/recursive-step branch
   * labels shown when a recursive CTE body is the standard two-branch
   * `UNION [ALL]` shape (§2.8 S14, decision 6).
   */
  cte: {
    /** Tooltip on the click-to-rename CTE name. */
    rename: 'Click to rename CTE',
    /** Tooltip on the enabled remove button when the CTE is not referenced. */
    remove: 'Remove this CTE',
    /** Tooltip on the enabled remove button when the CTE IS referenced downstream. */
    removeReferenced: 'Remove this CTE (it is referenced — you will be asked to confirm)',
    /** Tooltip on the focus/drill button that opens the CTE body full-view. */
    focusBody: 'Focus on this CTE body',
    /** Label above the first branch (the base case) of a standard recursive body. */
    anchorBranch: 'Anchor query',
    /** Label above the second branch (the self-referencing step) of a standard recursive body. */
    recursiveStep: 'Recursive step',
  },

  /**
   * DDL compact-card copy (§2.9 S13) — the summary card shown in place of the
   * DML edit sections when the cursor statement is DDL. `facts` formatters
   * render `DdlCardModel.facts` lines; `capability` is the honest "structural
   * editing isn’t here" line every READ-ONLY card variant shows.
   *
   * `dropCapability` replaces it on the editable DROP arm only: a DROP has no
   * structure to edit, so the line there says what the arm DOES offer instead
   * of repeating a limitation the user can't act on.
   */
  ddlCard: {
    capability:
      'This is a DDL statement — structural editing isn’t available in the quick editor.',
    /**
     * The full tab's one explanatory line on the read-only DDL card (the card
     * replaced a banner + a hint box + this line that all said the same
     * thing). Names the two ways forward that the card's buttons offer.
     */
    fullHint: (canDesignTable: boolean): string =>
      canDesignTable
        ? 'The Visual Query Editor does not edit DDL. Open the statement in the SQL editor, or open the table in Design table.'
        : 'The Visual Query Editor does not edit DDL. Open the statement in the SQL editor.',
    dropCapability: 'Review this DROP and check what depends on it.',
    facts: {
      columns: (n: number): string => `${n} column${n === 1 ? '' : 's'}`,
      addsColumn: (name: string): string => `Adds column ${name}`,
      dropsColumn: (name: string): string => `Drops column ${name}`,
      renamesColumn: (from: string, to: string): string => `Renames ${from} → ${to}`,
      otherChanges: (n: number): string => `${n} other change${n === 1 ? '' : 's'}`,
      onTable: (name: string): string => `On ${name}`,
      uniqueIndex: 'Unique index',
      indexColumns: (n: number): string => `${n} column${n === 1 ? '' : 's'}`,
      parameters: (n: number): string => `${n} parameter${n === 1 ? '' : 's'}`,
      enumValues: (n: number): string => `${n} enum value${n === 1 ? '' : 's'}`,
      baseType: (t: string): string => `Base type ${t}`,
      privileges: (p: number, g: number): string =>
        `${p} privilege${p === 1 ? '' : 's'} to ${g} grantee${g === 1 ? '' : 's'}`,
      /** Trigger timing + event list, e.g. "Before insert, update". Shown only
       *  when the trigger's parked seed carries both fields. */
      triggerFires: (timing: string, events: readonly string[]): string => {
        const label = timing === 'instead-of'
          ? 'Instead of'
          : `${timing.charAt(0).toUpperCase()}${timing.slice(1)}`;
        return `${label} ${events.join(', ')}`;
      },
    },
    actions: {
      designTable: 'Design table',
      /** Compact DDL popover: closes the card (the statement is already in the SQL editor). */
      close: 'Close',
    },
    tooltips: {
      designTableCreate: 'Open this CREATE TABLE in the Table Designer',
      designTableAlter: 'Open this table in the Table Designer — reads the live table',
      designTableNoConnection:
        'Design this table · no database is connected · connect to open the Table Designer',
      /** Full-tab DDL card Edit as SQL — opens the statement in the SQL editor. */
      editAsSql: 'Open this statement in the SQL editor',
      /** Compact-card Close — closes the popover and focuses the same statement in the SQL editor. */
      close: 'Close this card and go back to the statement in the SQL editor',
    },
  },

  /**
   * The DML target row's "Change table" swap (UPDATE / DELETE / MERGE full
   * views). Replaced a × that could only leave the statement without a target.
   */
  dmlTarget: {
    changeTable: 'Change table',
    searchPlaceholder: 'Search tables…',
    emptyMessage: 'No tables in this database.',
    disabled: {
      notReady:
        'Change the target table · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”',
      noTables:
        'Change the target table · the table list hasn’t loaded yet · wait for the schema or use “Reload schema”',
      aliasTarget:
        'Change the target table · the target is an alias of a table in FROM · change it with “Edit as SQL”',
      targetList:
        'Change the target table · this DELETE names its targets before FROM · change them with “Edit as SQL”',
    },
  },

  /** DML column pickers — SET assignment columns and INSERT column-list entries. */
  dmlColumns: {
    searchPlaceholder: 'Search or type a column…',
    notInTable: 'not in table',
    columnsUnknown: 'Type a column name',
    unknownColumn: (column: string, table: string) => `${table} has no column ${column}`,
    changeColumn: 'Change column',
    valueLabel: (column: string) => `Value for ${column}`,
    commentHint: 'Comment — kept on edit',
    textExample: "e.g. 'text'",
    valueHint: 'value or expression',
  },

  /**
   * Compact-popover degrade-card message fallbacks — shown when the cursor
   * statement (or a drilled subquery) is a shape the compact surface can't
   * safely edit and `model.degrade` didn't produce a reason-specific message
   * (§ 1.6 truthful copy: never claims the shape "is edited in the full
   * editor" when the real state is "can't be edited here yet").
   */
  degrade: {
    statementFallback:
      'This statement can’t be edited in the quick editor — open the full editor.',
    subqueryFallback:
      'This subquery can’t be edited in the quick editor — open the full editor.',
  },

  /**
   * Never-silent teaching copy (VQE Polish — EDG-11..23). Shown on the small
   * gutter / inline indicator that stands in for the hidden pencil when the
   * cursor statement can't be edited visually. Each string TEACHES per the
   * governing directive: what the construct is · why it can't be edited
   * visually · what to do next. `parseError` optionally interpolates the
   * failing token; the location-free form is used when the parser doesn't
   * surface a token. `label` is the terse chip caption for the inline bar (the
   * gutter variant is icon-only). Sibling of `degrade` (popover fallbacks) —
   * this pair covers the statement BEFORE the popover opens.
   */
  unavailable: {
    parseError: (near?: string): string =>
      near
        ? `Can’t show the visual editor — syntax error near “${near}”. Fix the SQL to edit it visually; you can still edit it as text here.`
        : 'Can’t show the visual editor — this statement has a syntax error. Fix the SQL to edit it visually; you can still edit it as text here.',
    procedural:
      'This is a procedural block (BEGIN…END / PL/SQL / DO) — it runs as SQL. Visual editing covers SELECT, INSERT, UPDATE, DELETE and MERGE; edit this block as text.',
    setOpPrecedence:
      'Mixed set-operator precedence can’t be charted visually — add explicit parentheses to group them, or edit as SQL.',
    /**
     * X1-T27 fix round 1 (ITEM 2) — parity with `setOpPrecedence`: a SQL/PGQ
     * GRAPH_TABLE query has no editable structured surface, so the inline
     * pencil hides (same as set-precedence), but the reason must still be
     * taught here rather than going silent.
     */
    graphTable:
      'Graph queries (GRAPH_TABLE) aren’t visually editable — use the SQL editor to change this statement.',
    /** Terse chip captions for the inline-bar variant (icon + label). */
    label: {
      parseError: 'Syntax error',
      procedural: 'SQL-only block',
      setOpPrecedence: 'Mixed set operators',
      graphTable: 'Graph query',
    },
  },

  /** Workspace-shell copy (statement indicator, registration stub). */
  workspace: {
    /** Statement-picker trigger label, e.g. "Statement 2 of 3". */
    statementPickerLabel: (activeNum: number, stmtCount: number): string =>
      `Statement ${activeNum} of ${stmtCount}`,
    /** Human-readable label for a statement kind, shown in the picker menu. */
    statementKindLabel: (kind: StatementKind): string =>
      STATEMENT_KIND_LABELS[kind] ?? 'Statement',
    /**
     * Amber "results are stale" notice shown in the results-panel tab bar
     * (`vqe-results-stale`) after the active statement's text changes
     * post-execution (Plan 6 §3.3 S8, decision 2). Cleared by the next run.
     */
    resultsStale:
      'Results are from an earlier version of this query — run again to refresh.',
  },

  /** Toast copy. */
  toast: {
    /** Inline status shown by the surface that made the edit (full-tab header, compact footer). */
    successTitle: 'Edit applied',
    /** The status's undo button — same undo as Cmd/Ctrl+Z on that surface. */
    undo: 'Undo',
    /** Tooltip / hint next to the undo button; `chord` is the platform label (⌘Z / Ctrl+Z). */
    undoHint: (chord: string): string => `${chord} to undo`,
    /** Short heading for the every-time commit-failure toast. */
    failureTitle: 'Couldn’t apply the edit',
    commitFailure: (reason: string): string =>
      `Couldn’t apply the edit: ${reason}. Your SQL is unchanged.`,
    /**
     * Failure reason recorded by `commitEdit`'s live-source guard (P12 Task 4)
     * when the authoritative buffer moved again between resolving the edit and
     * splicing it — re-applying the offset-based edits onto the moved buffer
     * would corrupt the SQL, so nothing is spliced. Phrased as a reason fragment
     * because the toast wraps it with `commitFailure`, which already appends
     * “Your SQL is unchanged.”
     */
    staleBuffer: 'the buffer changed while the edit was being prepared — try again',
  },

  /**
   * Sentence-case add/remove button labels. Real Sentence case: only the first
   * word is capitalized, plus acronyms (CTE, AND, OR, WHEN).
   */
  actions: {
    addColumn: 'Add column',
    /** Compact-view inline variant of `addColumn` — sits in the chip row. */
    addColumnShort: 'Add',
    /**
     * Section-level WHERE/HAVING add button (sits below the pills) — appends a
     * NEW top-level predicate. Named "Add condition" (not a bare "Add") so it
     * reads distinctly from the "+ or" / "+ and" chips on the conditions,
     * which instead group an existing condition with a new one (SEL-08). The
     * AND/OR choice lives in the picker header.
     */
    addFilterShort: 'Add condition',
    /**
     * WHERE/HAVING section-level "Clear all" (report G6) — removes the whole
     * clause in one edit. Sits beside `addFilterShort` (a query-changing
     * action, not a view control), shown once the clause has two or more
     * conditions.
     */
    clearAll: 'Clear all',
    /** Empty-state "+ Add limit" affordance in the Limit card (SEL-08/SEL-11). */
    addLimit: 'Add limit',
    addJoin: 'Add join',
    /**
     * SELECT FROM-section label for adding a second comma-separated table. It
     * is named "cross-join" (not "table") because adding a bare table there
     * produces a Cartesian product — `[+ add join]` is the FK-driven path for
     * related tables.
     */
    addCrossJoin: 'Add cross-join',
    addTable: 'Add table',
    addCte: 'Add CTE',
    addSort: 'Sort by column',
    addGroupBy: 'Group by column',
    addRow: 'Add row',
    addWhenBranch: 'Add WHEN',
    /** MERGE `[+ Add WHEN]` menu item labels (§2.5 S7) — sentence-cased verb + the SQL keywords they emit. */
    whenMatchedUpdate: 'WHEN MATCHED → UPDATE',
    whenMatchedDelete: 'WHEN MATCHED → DELETE',
    whenNotMatchedInsert: 'WHEN NOT MATCHED → INSERT',
    addAssignment: 'Add assignment',
    addSource: 'Add source',
    addValue: 'Add value',
    addWindow: 'Add window',
    /** Enabled raw-text edit button on a WindowSpec's expanded OVER body. */
    editOver: 'Edit OVER',
    /** Adds every missing non-aggregate column to GROUP BY in one click. */
    fixAllGroupBy: 'Fix all',
    /** Expands every CTE card in the WITH clause at once. */
    expandAllCtes: 'Expand all CTEs',
    /** Collapses every CTE card in the WITH clause at once. */
    collapseAllCtes: 'Collapse all CTEs',
    remove: 'Remove',
    /** Escape-hatch CTA that opens the raw-SQL editor. Appears in the workspace
     * header, fallback panels (read-only / parse-error), and disabled-control
     * tooltips. */
    editAsSql: 'Edit as SQL',
    /** Compact-popover CTA that promotes the cursor statement (or a degraded
     * drill) to the full Visual Query Editor workspace. */
    openFullEditor: 'Open full editor',
    /** Opens the ON CONFLICT conflict-target column picker (§2.4 S6b). */
    editConflictTarget: 'Edit target',
    /** Confirms the conflict-target picker's selection. */
    applyConflictTarget: 'Apply',
    /** Dismisses the conflict-target picker without committing. */
    cancelConflictTarget: 'Cancel',
  },

  /**
   * Structured window (OVER) editor copy (Task §4b-i — ANA-05/07/09/14/16/18/
   * 20/22). Replaces the opaque OVER textarea with real PARTITION BY / ORDER BY
   * / frame pickers. `frameCapabilityNote` is a truthful standing note (not a
   * hard block): GROUPS isn't universal, and a RANGE numeric offset needs
   * exactly one ORDER BY key and isn't supported on SQL Server. The engine is
   * known at edit time, so these are stated as plain facts — no unverifiable
   * server-version hedge (unlike `capability.merge.versionHint`).
   */
  window: {
    /** Section header hints for the empty partition / order / frame slots. */
    noPartition: 'No partitions — the window spans every row.',
    noOrder: 'No sort — the frame follows input order.',
    noFrame: 'No explicit frame — the engine default applies.',
    /** Enabled add-affordance tooltips. */
    addPartition: 'Add a PARTITION BY column',
    addOrder: 'Add an ORDER BY column',
    addFrame: 'Add frame',
    removeFrame: 'Remove the frame — fall back to the engine default',
    /** Empty column-chooser message when nothing is in scope. */
    noColumnsInScope: 'No columns in scope — add the table to FROM/JOIN, or edit the OVER clause as text.',
    /**
     * Truthful, standing frame capability note (never a hard block). Names the
     * two determinate facts a guided edit can otherwise trip silently: GROUPS
     * isn’t universal, and a RANGE numeric offset needs EXACTLY ONE ORDER BY key
     * and isn’t supported on SQL Server at all. Stated as plain facts (the engine
     * is known) — no "some engines" / server-version hedge (Lane C-2). This is
     * what keeps the multi-key and mssql RANGE-offset cases non-silent.
     */
    frameCapabilityNote:
      'GROUPS frames aren’t supported on every engine, and RANGE offset frames (N PRECEDING / N FOLLOWING) need exactly one ORDER BY key and aren’t supported on SQL Server — confirm your engine supports the frame you pick before running.',
    /**
     * Shown when a RANGE/GROUPS frame has NO ORDER BY at all. The N PRECEDING /
     * N FOLLOWING offsets require a sort, and GROUPS *mode* itself requires an
     * ORDER BY even without an offset (PG rejects GROUPS with no ORDER BY), so
     * the note names both — an engine-universal rule, not a dialect quirk (I4 /
     * Lane C-2). The multi-key / mssql RANGE cases are covered by the standing
     * capability note above instead, so this never makes a false "add a sort"
     * promise.
     */
    frameOffsetNeedsOrderBy:
      'RANGE and GROUPS frames need an ORDER BY — the N PRECEDING / N FOLLOWING offsets, and GROUPS mode itself, require a sort; add one above to use them.',
    /**
     * Shown when GROUPS is selected on an engine that doesn’t support it
     * (SQL Server, MySQL). The engine is KNOWN at edit time, so this is a plain
     * fact — not the "server version isn’t checked" hedge that fits truly
     * version-gated capabilities (I4).
     */
    frameGroupsUnsupported: (engineName: string): string =>
      `${engineName} doesn’t support GROUPS frames — pick ROWS or RANGE instead.`,
    /**
     * Read-only teaching line beside the EXCLUDE chip. An `EXCLUDE …` frame
     * spec is preserved verbatim (never structurally editable in the guided
     * picker — the sanctioned exotic-frame deferral), so this names the
     * construct and points at the raw-SQL escape hatch instead of leaving it
     * invisible (I2).
     */
    frameExcludeReadOnly:
      'EXCLUDE clause kept as-is — edit the OVER clause as SQL to change it.',
    /** Escape-hatch link from the structured editor to raw-text OVER editing. */
    editAsText: 'Edit as text',
    /** Note above the raw textarea when the OVER body can’t be charted structurally. */
    textFallbackNote:
      'This OVER clause has a part the visual editor can’t chart yet — edit the whole clause as text below.',
    /**
     * Named-window card (standalone `WINDOW w AS (…)` clause) copy — ANA-01/02/
     * 03. The card now edits its definitions for real (rename / remove / add /
     * structured body via the same WindowSpecEditor as inline OVER).
     */
    named: {
      /** Tooltip on the click-to-rename named-window name. Rename now
       *  coordinate-renames every `OVER <name>` reference to it — including
       *  un-aliased `count(*) OVER w` — by scanning the statement source
       *  (§4b-ii-fix²). When a reference is buried in a subquery / parentheses
       *  and can't be safely coordinated, the rename is refused rather than
       *  applied partially, so this promise never over-states what happens. */
      rename: 'Rename this window and its OVER references.',
      /** Consequence tooltip on the enabled named-window remove button. */
      remove:
        'Remove this window definition — any OVER clause that references it by name will error until you fix it.',
      /** Tooltip on the OVER-<w> reference note button that focuses the definition. */
      referenceFocus: 'Jump to this window’s definition in the WINDOW clause below.',
      /** Read-back shown at the OVER-<w> reference site when the referenced
       *  definition carries no PARTITION BY / ORDER BY / frame of its own. */
      referenceNoSpec: 'no partition or order',
      /** Read-back shown when the referenced window name isn’t defined in the clause. */
      referenceMissing: 'not defined in the WINDOW clause',
      /** Empty-body hint on a definition whose window is unbounded. */
      emptyBody: 'No partition or order — the window spans every row.',
    },
  },
  /** WHERE / HAVING view switch (Auto / Inline / Rail) segment tooltips. The
   *  Auto text restates `resolveAutoWhereView` (depth ≤ 1 and ≤ 4 leaves). */
  /**
   * The chip on a WHERE / HAVING condition (LeafConvertAdd) and at the end of a
   * group box: groups with a new condition by the named connector — the
   * opposite of the list's for a condition, the group's own at a group's end.
   * Rendered after the trigger's "+" icon, so the chip reads "+ or". Lower
   * case on purpose: it names the SQL connector, not an action verb.
   */
  conditionChip: {
    or: 'or',
    and: 'and',
  },
  /**
   * The SELECT's clause add row (ClauseAddRow): one row offering the EMPTY
   * optional clauses in SQL order — "Add  + Where  + Group by  + Order by
   * + Limit" — instead of a "— none" row per clause. LIMIT's entry is the
   * dialect keyword (Top / Limit / Fetch first).
   */
  clauseAdd: {
    lead: 'Add',
    where: 'Where',
    groupBy: 'Group by',
    having: 'Having',
    orderBy: 'Order by',
  },
  whereView: {
    auto: 'Inline for up to 4 conditions without nested groups, otherwise Rail',
    inline: 'All conditions in one wrapping row',
    rail: 'One condition per line, with groups indented under their AND/OR',
  },
} as const;

export type VqeCopy = typeof vqeCopy;
